import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useAuth } from "../auth";
import { dishTitle, useI18n } from "../lib/i18n";
import { MaterialPreview } from "../components/MaterialPreview";
import { VIDEO_ACCEPT } from "../lib/media";
import { inspectVideoFiles, sortVideoFiles } from "../lib/parts";
import { materialsOf, parseMaterialPath } from "../lib/materials";
import { mb, uploadMaterial, type UploadProgress } from "../lib/upload";
import type { DishDoc } from "../lib/types";

const functions = getFunctions(app, "asia-northeast1");
const updateDishMaterials = httpsCallable<
  { dishId: string; sourceVideoPaths: string[]; recipeDocPaths: string[]; reason?: string },
  { ok: boolean }
>(functions, "updateDishMaterials");

/**
 * 素材の差し替え。
 *
 * **既にある素材を上書きしない。** 追加は新しいオブジェクトとして置き、外した素材は
 * 品目から切り離すだけで消さない。こうしておくと、履歴から「当時どの素材で
 * 生成したのか」をそのまま開いて確かめられる（消してしまうと辿れない）。
 *
 * 動画の並び順は**結合順の正**なので、既存のパートと追加分を1つの並びとして
 * 扱う（別々の欄にすると、追加分を途中に差し込めない）。
 */

type Item =
  | { key: string; kind: "existing"; path: string; name: string }
  | { key: string; kind: "new"; file: File; name: string };

let seq = 0;
function newItem(file: File): Item {
  return { key: `new-${seq++}-${file.name}`, kind: "new", file, name: file.name };
}
function existingItem(path: string): Item {
  return { key: path, kind: "existing", path, name: parseMaterialPath(path).fileName };
}

function move(items: Item[], i: number, delta: number): Item[] {
  const j = i + delta;
  if (j < 0 || j >= items.length) return items;
  const next = [...items];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export function DishMaterials() {
  const { dishId } = useParams<{ dishId: string }>();
  const { profile, paths } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  const [dish, setDish] = useState<DishDoc | null>(null);
  const [videos, setVideos] = useState<Item[]>([]);
  const [docs, setDocs] = useState<Item[]>([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, UploadProgress>>({});
  const [preview, setPreview] = useState<string | null>(null);
  // 取り込み済みか。編集中に onSnapshot が来ても入力を巻き戻さないための門
  const loaded = useRef(false);
  // 送り終わったファイルの保存先。保存の後段（Callable）で弾かれたときに、
  // 2GB の動画をもう一度送らせないため（「処理中です」は普通に起きる）
  const uploaded = useRef<Record<string, string>>({});

  const isStaff = profile?.role === "admin" || profile?.role === "chef";

  useEffect(() => {
    if (!dishId || !paths) return;
    return onSnapshot(
      paths.dishDoc(dishId),
      (s) => {
        const d = s.exists() ? (s.data() as DishDoc) : null;
        setDish(d);
        // 編集中に上書きしない。開いた時点の内容を1度だけ取り込む
        if (d && !loaded.current) {
          loaded.current = true;
          const m = materialsOf(d);
          setVideos(m.videos.map(existingItem));
          setDocs(m.docs.map(existingItem));
        }
      },
      (e) => setError(e.message),
    );
  }, [dishId, paths]);

  const warnings = useMemo(() => inspectVideoFiles(videos.map((v) => ({ name: v.name }))), [videos]);
  const addedCount = videos.filter((v) => v.kind === "new").length + docs.filter((d) => d.kind === "new").length;

  function addVideos(files: File[]) {
    // 追加分どうしの並びだけ整える。既存パートとの前後は人が決める
    setVideos((prev) => [...prev, ...sortVideoFiles(files).map(newItem)]);
  }

  async function onSave() {
    if (!dishId || !dish) return;
    if (videos.length === 0) {
      setError(t("調理動画は1本以上必要です。"));
      return;
    }
    if (
      dish.currentAnalysisPath &&
      !window.confirm(
        t("素材を差し替えても、出ているマニュアルは自動では作り直されません。差し替えた後に「作り直す」を実行してください。よろしいですか?"),
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const send = async (item: Item, kind: "video" | "doc"): Promise<string> => {
        if (item.kind === "existing") return item.path;
        const already = uploaded.current[item.key];
        if (already) return already;
        const path = await uploadMaterial(dishId, item.file, kind, (p, st) =>
          setProgress((prev) => ({ ...prev, [p]: st })),
        );
        uploaded.current[item.key] = path;
        return path;
      };

      const videoPaths: string[] = [];
      for (const v of videos) videoPaths.push(await send(v, "video"));
      const docPaths: string[] = [];
      for (const d of docs) docPaths.push(await send(d, "doc"));
      await updateDishMaterials({
        dishId,
        sourceVideoPaths: videoPaths,
        recipeDocPaths: docPaths,
        reason: reason.trim() || undefined,
      });
      navigate(`/dishes/${dishId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  if (!dishId) return null;
  if (!dish) {
    return (
      <div className="page">
        <p className="muted">{error ? "" : t("読み込み中…")}</p>
        {error && <p className="error">{error}</p>}
      </div>
    );
  }
  if (!isStaff) {
    return (
      <div className="page">
        <p><Link to={`/dishes/${dishId}`}>← {dishTitle(dish, lang)}</Link></p>
        <p className="error">{t("素材の差し替えは管理者と料理長のみ行えます。")}</p>
      </div>
    );
  }

  const items = Object.entries(progress);

  return (
    <div className="page">
      <p><Link to={`/dishes/${dishId}`}>← {dishTitle(dish, lang)}</Link></p>
      <h1>{t("素材の差し替え")}</h1>
      <p className="muted small">
        {t("外した素材は消えません（履歴から中身を確認できます）。誰が何を差し替えたかは実行履歴に残ります。")}
      </p>

      <div className="card">
        <h2>{t("調理動画")}</h2>
        <p className="muted small">
          {t("並び順がそのまま結合順になります。カメラが自動で分割したファイルは、撮影した順に並べてください。")}
        </p>
        {videos.length === 0 && <p className="error">{t("調理動画がありません。1本以上必要です。")}</p>}
        <ol className="parts">
          {videos.map((v, i) => (
            <li key={v.key}>
              <span className="no">{videos.length > 1 ? t("{n}本目", { n: i + 1 }) : t("1本のみ")}</span>
              <span className="nm">
                {v.kind === "existing" ? (
                  <button type="button" className="link file" onClick={() => setPreview(v.path)}>
                    {v.name}
                  </button>
                ) : (
                  v.name
                )}
                {v.kind === "new" && <span className="badge sub">{t("追加")}</span>}
              </span>
              <span className="sz">{v.kind === "new" ? mb(v.file.size) : ""}</span>
              <span className="mvs">
                <button type="button" disabled={i === 0 || busy} onClick={() => setVideos((p) => move(p, i, -1))}>↑</button>
                <button type="button" disabled={i === videos.length - 1 || busy} onClick={() => setVideos((p) => move(p, i, 1))}>↓</button>
                <button type="button" disabled={busy} onClick={() => setVideos((p) => p.filter((x) => x.key !== v.key))}>{t("外す")}</button>
              </span>
            </li>
          ))}
        </ol>
        {warnings.length > 0 && (
          <div className="warn">
            {warnings.map((w) => (
              <div key={w}>⚠ {t(w)}</div>
            ))}
          </div>
        )}
        <label htmlFor="addVideos">{t("動画を追加")}</label>
        <input
          id="addVideos"
          type="file"
          accept={VIDEO_ACCEPT}
          multiple
          disabled={busy}
          onChange={(e) => {
            addVideos([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
        />
      </div>

      <div className="card">
        <h2>{t("レシピ・メモ")}</h2>
        {docs.length === 0 ? (
          <p className="muted">{t("ありません。")}</p>
        ) : (
          <ul className="files">
            {docs.map((d) => (
              <li key={d.key}>
                {d.kind === "existing" ? (
                  <button type="button" className="link file" onClick={() => setPreview(d.path)}>
                    {d.name}
                  </button>
                ) : (
                  <span>{d.name}</span>
                )}
                {d.kind === "new" && <span className="badge sub">{t("追加")}</span>}
                <button type="button" className="link" disabled={busy} onClick={() => setDocs((p) => p.filter((x) => x.key !== d.key))}>
                  {t("外す")}
                </button>
              </li>
            ))}
          </ul>
        )}
        <label htmlFor="addDocs">{t("資料を追加(PDF / 画像・複数可)")}</label>
        <input
          id="addDocs"
          type="file"
          accept="application/pdf,image/*"
          multiple
          disabled={busy}
          onChange={(e) => {
            setDocs((prev) => [...prev, ...[...(e.target.files ?? [])].map(newItem)]);
            e.target.value = "";
          }}
        />
      </div>

      <div className="card">
        <h2>{t("変更の記録")}</h2>
        <label htmlFor="reason">{t("変更の理由(任意)")}</label>
        <textarea
          id="reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("例: 手元が写っていない区間があったため、撮り直した動画に差し替え")}
          disabled={busy}
        />
        <p className="muted small">
          {t("日時・操作者・変更内容は自動で残ります。理由を書いておくと、後から見た人が「なぜ変わったか」を辿れます。")}
        </p>
        <button onClick={onSave} disabled={busy || videos.length === 0}>
          {busy ? (addedCount > 0 ? t("アップロード中…") : t("保存中…")) : t("この内容で差し替える")}
        </button>{" "}
        <Link className="link" to={`/dishes/${dishId}`}>{t("やめる")}</Link>
        {dish.currentAnalysisPath && (
          <p className="muted small">
            {t("差し替えても、出ているマニュアルは自動では作り直されません。差し替えた後に品目の画面から「作り直す」を実行してください。")}
          </p>
        )}
        {error && <p className="error">{error}</p>}
      </div>

      {items.length > 0 && (
        <div className="card">
          <h2>{t("アップロード状況")}</h2>
          {items.map(([key, p]) => (
            <div key={key} className="progress-row">
              <div className="progress-label">
                <span>{p.name}</span>
                <span className="muted">{p.done ? t("完了") : `${mb(p.transferred)} / ${mb(p.total)}`}</span>
              </div>
              <progress value={p.transferred} max={p.total || 1} />
            </div>
          ))}
        </div>
      )}

      {preview && <MaterialPreview dishId={dishId} path={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
